import React, { useState, useEffect } from 'react';
import { User } from '../../../types/organizations';
import PhoneNumberInput from '../../PhoneNumberInput';

interface UserFormData {
  first_name: string;
  last_name: string;
  email_address: string;
  phone_number: string;
  country_code: string;
  job_title: string;
  department: string;
  is_primary_contact: boolean;
  is_active: boolean;
}

interface UserModalProps {
  isOpen: boolean;
  mode: 'add' | 'edit';
  user?: User | null;
  organizationId: string;
  onClose: () => void;
  onSubmit: (formData: UserFormData) => Promise<void>;
}

const UserModal: React.FC<UserModalProps> = ({
  isOpen,
  mode,
  user,
  organizationId,
  onClose,
  onSubmit
}) => {
  const [formData, setFormData] = useState<UserFormData>({
    first_name: '',
    last_name: '',
    email_address: '',
    phone_number: '',
    country_code: '+65',
    job_title: '',
    department: '',
    is_primary_contact: false,
    is_active: true
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Initialize form data when user changes (for edit mode)
  useEffect(() => {
    if (mode === 'edit' && user) {
      setFormData({
        first_name: user.first_name,
        last_name: user.last_name,
        email_address: user.email_address,
        phone_number: user.phone_number || '',
        country_code: user.country_code || '+65',
        job_title: user.job_title || '',
        department: user.department || '',
        is_primary_contact: user.is_primary_contact,
        is_active: user.is_active
      });
    } else if (mode === 'add') {
      // Reset form for add mode
      setFormData({
        first_name: '',
        last_name: '',
        email_address: '',
        phone_number: '',
        country_code: '+65',
        job_title: '',
        department: '',
        is_primary_contact: false,
        is_active: true
      });
    }
  }, [mode, user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      await onSubmit(formData);
      onClose();
    } catch (err: any) {
      setError(err.message || `Failed to ${mode} user`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof UserFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-6">
            {mode === 'add' ? 'Add New Employee' : 'Edit Employee'}
          </h3>
          
          {error && (
            <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-2 gap-8">
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <label className="block text-sm font-medium text-gray-700">First Name *</label>
                  <input
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => handleInputChange('first_name', e.target.value)}
                    className="w-64 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div className="flex justify-between items-center">
                  <label className="block text-sm font-medium text-gray-700">Last Name *</label>
                  <input
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => handleInputChange('last_name', e.target.value)}
                    className="w-64 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div className="flex justify-between items-center">
                  <label className="block text-sm font-medium text-gray-700">Email Address *</label>
                  <input
                    type="email"
                    value={formData.email_address}
                    onChange={(e) => handleInputChange('email_address', e.target.value)}
                    className="w-64 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                
                <div className="flex justify-between items-center">
                  <label className="block text-sm font-medium text-gray-700">Phone Number</label>
                  <PhoneNumberInput
                    countryCode={formData.country_code}
                    phoneNumber={formData.phone_number}
                    onCountryCodeChange={(code) => handleInputChange('country_code', code)}
                    onPhoneNumberChange={(number) => handleInputChange('phone_number', number)}
                    className="w-64"
                  />
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <label className="block text-sm font-medium text-gray-700">Job Title</label>
                  <input
                    type="text"
                    value={formData.job_title}
                    onChange={(e) => handleInputChange('job_title', e.target.value)}
                    className="w-64 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div className="flex justify-between items-center">
                  <label className="block text-sm font-medium text-gray-700">Department</label>
                  <input
                    type="text"
                    value={formData.department}
                    onChange={(e) => handleInputChange('department', e.target.value)}
                    className="w-64 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div className="flex justify-between items-center">
                  <label className="block text-sm font-medium text-gray-700">Primary Contact</label>
                  <div className="w-64 flex justify-start">
                    <input
                      type="checkbox"
                      checked={formData.is_primary_contact}
                      onChange={(e) => handleInputChange('is_primary_contact', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>
                </div>
                
                <div className="flex justify-between items-center">
                  <label className="block text-sm font-medium text-gray-700">Active</label>
                  <div className="w-64 flex justify-start">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => handleInputChange('is_active', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-8">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Saving...' : (mode === 'add' ? 'Add Employee' : 'Update Employee')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserModal;
